import { Observable, pipe, of, BehaviorSubject } from 'rxjs';
import { switchMap, tap, map, catchError, filter, take, finalize } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { sortBy, extend } from 'lodash';
import { Page } from '@app/models/page';
import * as i0 from "@angular/core";
// eslint-disable-next-line no-shadow
export var CacheEvent;
(function (CacheEvent) {
    CacheEvent[CacheEvent["IDLE"] = 0] = "IDLE";
    CacheEvent[CacheEvent["UPDATE"] = 1] = "UPDATE";
})(CacheEvent || (CacheEvent = {}));
export class Cache {
    constructor() {
        this.tables = {};
        this.cacheTable = {};
        this.caches = {};
        window.cache = this;
    }
    register(name, model, expire) {
        this.tables[name] = {};
        this.cacheTable[name] = {
            n: name,
            t: 0,
            d: null,
            s: [],
        };
        this.caches[name] = {
            model,
            expire: expire || 600,
            loading: 0,
            changes: new BehaviorSubject(CacheEvent.IDLE),
        };
    }
    getTable(cache) {
        return this.tables[cache];
    }
    /**
     * Searches for and returns a model in the cache. If it does not exist
     * in the cache, subscribes to the provided resolver and caches the result.
     * @param cache The name of the cache (maps to the database collection name)
     * @param id The id of the model to get
     * @param resolver An observable that resolves to the model if it is not found
     */
    getOne(cache, id, resolver) {
        const tbl = this.getTable(cache);
        return of(tbl[id]).pipe(switchMap((results) => {
            if (!results) {
                const cacheInfo = this.caches[cache];
                cacheInfo.changes.next(CacheEvent.UPDATE);
                return resolver.req.pipe(this.insertPipe(cache, resolver, null, cacheInfo), finalize(() => cacheInfo.changes.next(CacheEvent.IDLE)), map(() => tbl[id]));
            }
            return of(results);
        }));
    }
    /**
     * Searches for and returns multiple models in the cache. If one does not exist
     * in the cache, subscribes to the provided resolver and caches the result.
     * @param cache The name of the cache (maps to the database collection name)
     * @param ids The ids of the models
     * @param resolver An observable that resolves to the model if it is not found
     */
    getSome(cache, ids, resolver) {
        let models = [];
        let noResult = false;
        const cacheQueryFn = () => {
            models = [];
            for (let i = 0; i < ids.length; i++) {
                const val = this.getTable(cache)[ids[i]];
                models.push(val);
                if (!val) {
                    noResult = true;
                }
            }
        };
        return this.waitUntilCacheIdle(this.caches[cache]).pipe(switchMap(() => {
            cacheQueryFn();
            if (noResult) {
                const cacheInfo = this.caches[cache];
                cacheInfo.changes.next(CacheEvent.UPDATE);
                return resolver.req.pipe(this.insertPipe(cache, resolver, null, cacheInfo), finalize(() => cacheInfo.changes.next(CacheEvent.IDLE)), map(() => {
                    cacheQueryFn();
                    return models;
                }));
            }
            return of(models);
        }));
    }
    /**
     * Searches for and joins multiple models in the cache. If one does not exist
     * in the cache, subscribes to the provided resolver and caches the result.
     * @param cache The name of the cache (maps to the database collection name)
     * @param ids The ids of the models
     * @param resolver An observable that resolves to the model if it is not found
     */
    joinSome(cache, models, idField, joinField, resolver) {
        let noResult = false;
        const cacheQueryFn = () => {
            for (let i = 0; i < models.length; i++) {
                let val;
                if (!models[i][joinField]) {
                    val = this.getTable(cache)[models[i][idField]];
                    models[i][joinField] = val;
                }
                if (!val) {
                    noResult = true;
                }
            }
        };
        return this.waitUntilCacheIdle(this.caches[cache]).pipe(switchMap(() => {
            cacheQueryFn();
            if (noResult) {
                const cacheInfo = this.caches[cache];
                cacheInfo.changes.next(CacheEvent.UPDATE);
                return resolver.req.pipe(this.insertPipe(cache, resolver, null, cacheInfo), finalize(() => cacheInfo.changes.next(CacheEvent.IDLE)), map(() => {
                    cacheQueryFn();
                    return models;
                }));
            }
            return of(models);
        }));
    }
    getPage(cache, page, resolver) {
        const cacheInfoQuery = this.cacheTable[cache];
        return this.waitUntilCacheIdle(this.caches[cache]).pipe(map(() => cacheInfoQuery), this.checkForPage(page, this.caches[cache]), switchMap((data) => {
            if (!data.page) {
                const cacheInfo = this.caches[cache];
                cacheInfo.changes.next(CacheEvent.UPDATE);
                return resolver.req.pipe(this.insertPipe(cache, resolver, data.info, this.caches[cache]), finalize(() => cacheInfo.changes.next(CacheEvent.IDLE)), map((d) => d.page));
            }
            return of(data.page);
        }), map((cachePage) => this.getPageQuery(cache, page, cachePage)));
    }
    /**
     * Returns all of the models in the cache. If the cache is expired,
     * subscribes to the provided resolver and caches the result.
     * @param cache The name of the cache (maps to the database collection name)
     * @param resolver An observable that resolves to the model if it is not found
     * @param sortField The field to sort the data on
     * @param sortDir The sort direction
     */
    getAll(cache, resolver, sortField, sortDir) {
        const cacheInfoQuery = this.cacheTable[cache];
        return this.waitUntilCacheIdle(this.caches[cache]).pipe(map(() => cacheInfoQuery), switchMap((results) => {
            if (this.cacheExpired(results)) {
                const cacheInfo = this.caches[cache];
                cacheInfo.changes.next(CacheEvent.UPDATE);
                return resolver.req.pipe(this.insertPipe(cache, resolver, results, cacheInfo), finalize(() => cacheInfo.changes.next(CacheEvent.IDLE)));
            }
            return of(null);
        }), map(() => {
            let models = sortBy(this.getTable(cache), sortField || 'id');
            if (sortDir === 'DESC') {
                models = models.reverse();
            }
            return models;
        }));
    }
    put(cache, models) {
        const cacheInfo = this.caches[cache];
        cacheInfo.changes.next(CacheEvent.UPDATE);
        const tbl = this.getTable(cache);
        for (const model of models) {
            tbl[model.id] = model;
        }
        cacheInfo.changes.next(CacheEvent.IDLE);
        return of(null);
    }
    mapToClassPipe(cache) {
        return pipe(map((models) => {
            if (Array.isArray(models)) {
                for (const obj of models) {
                    const model = Object.create(this.caches[cache].model);
                    for (const m of obj) {
                        if (obj.hasOwnProperty(m)) {
                            try {
                                model[m] = obj[m];
                            }
                            catch (_) { }
                        }
                    }
                }
            }
            else if (typeof models === 'object') {
                const model = Object.create(this.caches[cache].model);
                for (const m of models) {
                    if (models.hasOwnProperty(m)) {
                        try {
                            model[m] = models[m];
                        }
                        catch (_) { }
                    }
                }
            }
        }));
    }
    insertPipe(cache, resolver, cacheInfo, cacheLocalInfo) {
        return pipe(tap((data) => {
            const models = [];
            cacheLocalInfo.changes.next(CacheEvent.UPDATE);
            // Create and populate new models from API data
            for (const obj of data.data) {
                models.push(new cacheLocalInfo.model(obj));
            }
            // If the request is for all models, clear the table
            // and add new models. Otherwise, put all models into
            // table
            if (resolver.params && resolver.params.all) {
                this.tables[cache] = {};
            }
            for (const model of models) {
                this.tables[cache][model.id] = model;
            }
            if (cacheInfo) {
                const cacheUpdate = {};
                if (resolver.params && resolver.params.all) {
                    cacheUpdate.d = DateTime.local().toISO();
                    cacheUpdate.t = data.count;
                }
                else if (resolver.page) {
                    resolver.page.cursor = models[0][resolver.page.sortField];
                    cacheUpdate.s = this.filterSections(cache, cacheInfo).s;
                    cacheUpdate.s.push({
                        d: DateTime.utc().toISO(),
                        p: resolver.page.serialize(),
                    });
                }
                this.cacheTable[cacheInfo.n] = extend(this.cacheTable[cacheInfo.n], cacheUpdate);
            }
        }), map(() => resolver), tap(() => cacheLocalInfo.changes.next(CacheEvent.IDLE)), catchError((val) => {
            cacheLocalInfo.changes.next(CacheEvent.IDLE);
            throw val;
        }));
    }
    waitUntilCacheIdle(cache) {
        return cache.changes.pipe(filter((change) => change === CacheEvent.IDLE), take(1));
    }
    waitUntilCacheIdlePipe(cache) {
        return pipe(switchMap((data) => this.waitUntilCacheIdle(cache).pipe(map(() => data))));
    }
    cacheExpired(data) {
        const cache = this.caches[data.n];
        if (!data.d ||
            (data.d &&
                cache.expire !== Infinity &&
                DateTime.fromISO(data.d).plus({ seconds: cache.expire }) < DateTime.local())) {
            return true;
        }
        return false;
    }
    checkForPage(page, localCache) {
        return pipe(map((cacheInfo) => {
            // Check if entire collection is fetched
            if (cacheInfo.d &&
                DateTime.fromISO(cacheInfo.d) > DateTime.local().minus({ seconds: localCache.expire })) {
                return {
                    page: new Page(1, Infinity, page.sortField, page.sortDirection),
                    info: cacheInfo,
                };
            }
            // Find a valid page (either matching or a page that encompasses the input page)
            const match = cacheInfo.s.find((section) => section.p && page.compare(Page.deserialize(section.p)));
            return {
                page: match && match.p && Page.deserialize(match.p),
                info: cacheInfo,
            };
        }));
    }
    getPageQuery(cache, page, cachePage) {
        const offset = (page.page - cachePage.page) * page.pageSize || 0;
        if (cachePage.cursor) {
            if (page.sortDirection === 'ASC') {
                const filtered = Object.values(this.getTable(cache)).filter((model) => model[page.sortField] >= cachePage.cursor);
                return sortBy(filtered, page.sortField).slice(offset, offset + page.limit);
            }
            else {
                const filtered = Object.values(this.getTable(cache)).filter((model) => model[page.sortField] <= cachePage.cursor);
                return sortBy(filtered, page.sortField)
                    .reverse()
                    .slice(offset, offset + page.limit);
            }
        }
        else {
            if (page.sortDirection === 'ASC') {
                return Object.values(this.getTable(cache)).slice(offset, offset + page.limit);
            }
            else {
                return Object.values(this.getTable(cache))
                    .reverse()
                    .slice(offset, offset + page.limit);
            }
        }
    }
    filterSections(cache, cacheInfo) {
        // eslint-disable-next-line max-len
        cacheInfo.s = cacheInfo.s.filter((section) => DateTime.fromISO(section.d) > DateTime.local().minus({ second: this.caches[cache].expire }));
        return cacheInfo;
    }
}
Cache.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function Cache_Factory() { return new Cache(); }, token: Cache, providedIn: "root" });
